import { Component } from '@theme/component';

/**
 * Homepage review slider (sections/home-reviews-slider.liquid).
 * The track is a native scroll-snap row, so touch swipe and wheel work without
 * script; this adds the arrows, the progress line, arrow-key steps and the
 * optional slow autoplay.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scrolling list of slides.
 * @property {HTMLElement} controls - Progress line and arrows (hidden until ready).
 * @property {HTMLButtonElement} previous - Previous button.
 * @property {HTMLButtonElement} next - Next button.
 * @property {HTMLElement[]} [slides] - The slides.
 *
 * @extends {Component<Refs>}
 */
class ReviewsCarousel extends Component {
  requiredRefs = ['track', 'controls', 'previous', 'next'];

  /** Autoplay interval in ms. */
  static INTERVAL = 7000;

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {number | null} */
  #timer = null;

  /** @type {number | null} */
  #frame = null;

  #paused = false;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  #setup() {
    this.#teardown();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const { track } = this.refs;

    track.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal });

    for (const type of ['pointerenter', 'focusin']) {
      this.addEventListener(type, () => this.#pause(), { signal });
    }
    this.addEventListener('pointerleave', () => {
      if (!this.matches(':focus-within')) this.#resume();
    }, { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) this.#resume();
      },
      { signal }
    );
    this.#reducedMotion.addEventListener('change', () => this.#restartAutoplay(), { signal });

    this.#update();
    this.#restartAutoplay();
  }

  #teardown() {
    this.#abort?.abort();
    this.#abort = null;
    this.#stopAutoplay();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = null;
  }

  /** Width of one step: a slide plus the gap. */
  get #step() {
    const { track } = this.refs;
    const slide = this.refs.slides?.[0];
    if (!slide) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  get #direction() {
    return getComputedStyle(this).direction === 'rtl' ? -1 : 1;
  }

  get #behavior() {
    return this.#reducedMotion.matches ? 'auto' : 'smooth';
  }

  /** Scroll position from the start edge (positive in LTR and RTL). */
  get #position() {
    return Math.abs(this.refs.track.scrollLeft);
  }

  get #maxPosition() {
    const { track } = this.refs;
    return Math.max(0, track.scrollWidth - track.clientWidth);
  }

  previous() {
    this.#scrollBySteps(-1);
  }

  next() {
    this.#scrollBySteps(1);
  }

  /**
   * Arrow keys move one slide at a time while the track has focus.
   * @param {KeyboardEvent} event
   */
  onTrackKeydown(event) {
    const forward = this.#direction === 1 ? 'ArrowRight' : 'ArrowLeft';
    const backward = this.#direction === 1 ? 'ArrowLeft' : 'ArrowRight';
    if (event.key !== forward && event.key !== backward) return;

    event.preventDefault();
    this.#scrollBySteps(event.key === forward ? 1 : -1);
  }

  /** @param {number} steps */
  #scrollBySteps(steps) {
    this.refs.track.scrollBy({ left: steps * this.#step * this.#direction, behavior: this.#behavior });
  }

  #scheduleUpdate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#update();
    });
  };

  /** Arrow states, progress line, and whether controls are needed at all. */
  #update() {
    const { track, controls, previous, next } = this.refs;
    const max = this.#maxPosition;
    const position = this.#position;
    const scrollable = max > 1;

    controls.hidden = !scrollable;
    previous.disabled = position <= 1;
    next.disabled = position >= max - 1;

    const size = (track.clientWidth / track.scrollWidth) * 100;
    const start = (position / track.scrollWidth) * 100;
    this.style.setProperty('--cs-progress-size', `${size}%`);
    this.style.setProperty('--cs-progress-start', `${start}%`);
  }

  #restartAutoplay() {
    this.#stopAutoplay();
    if (!this.hasAttribute('autoplay') || this.#reducedMotion.matches) return;

    this.#timer = window.setInterval(() => {
      if (this.#paused || document.hidden) return;
      if (this.#position >= this.#maxPosition - 1) {
        this.refs.track.scrollTo({ left: 0, behavior: this.#behavior });
      } else {
        this.next();
      }
    }, ReviewsCarousel.INTERVAL);
  }

  #stopAutoplay() {
    if (this.#timer) window.clearInterval(this.#timer);
    this.#timer = null;
  }

  #pause() {
    this.#paused = true;
  }

  #resume() {
    this.#paused = false;
  }
}

if (!customElements.get('cs-reviews-carousel')) {
  customElements.define('cs-reviews-carousel', ReviewsCarousel);
}
